import type { LngLatEle } from './geo'

export interface GpxTrack {
  name: string
  /** [lng, lat, ele] — ele may be 0 when unknown */
  coords: LngLatEle[]
  /** epoch ms per point, when present */
  times?: number[]
  waypoints?: { name: string; lng: number; lat: number }[]
}

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

/** Serialize a track to GPX 1.1. */
export function toGpx(track: GpxTrack, creator = 'Traverse'): string {
  const lines: string[] = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<gpx version="1.1" creator="${esc(creator)}" xmlns="http://www.topografix.com/GPX/1/1">`,
    `  <metadata><name>${esc(track.name)}</name></metadata>`,
  ]
  for (const w of track.waypoints ?? []) {
    lines.push(`  <wpt lat="${w.lat.toFixed(6)}" lon="${w.lng.toFixed(6)}"><name>${esc(w.name)}</name></wpt>`)
  }
  lines.push(`  <trk><name>${esc(track.name)}</name><trkseg>`)
  track.coords.forEach((c, i) => {
    const time = track.times?.[i]
    lines.push(
      `    <trkpt lat="${c[1].toFixed(6)}" lon="${c[0].toFixed(6)}">` +
        `<ele>${(c[2] ?? 0).toFixed(1)}</ele>` +
        (time ? `<time>${new Date(time).toISOString()}</time>` : '') +
        `</trkpt>`,
    )
  })
  lines.push('  </trkseg></trk>', '</gpx>')
  return lines.join('\n')
}

/** Parse GPX (tracks or routes) using DOMParser. Throws on invalid input. */
export function parseGpx(xml: string): GpxTrack {
  const doc = new DOMParser().parseFromString(xml, 'application/xml')
  if (doc.querySelector('parsererror')) throw new Error('Not a valid GPX file')

  const name =
    doc.querySelector('trk > name')?.textContent?.trim() ||
    doc.querySelector('metadata > name')?.textContent?.trim() ||
    'Imported tour'

  const pts = [...doc.querySelectorAll('trkpt')]
  const routePts = pts.length ? pts : [...doc.querySelectorAll('rtept')]
  if (routePts.length < 2) throw new Error('GPX file contains no track')

  const coords: LngLatEle[] = []
  const times: number[] = []
  let hasTime = true
  for (const pt of routePts) {
    const lat = parseFloat(pt.getAttribute('lat') ?? '')
    const lon = parseFloat(pt.getAttribute('lon') ?? '')
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue
    const ele = parseFloat(pt.querySelector('ele')?.textContent ?? '')
    coords.push([lon, lat, Number.isFinite(ele) ? ele : 0])
    const t = pt.querySelector('time')?.textContent
    if (t && hasTime) {
      const ms = Date.parse(t)
      if (Number.isFinite(ms)) times.push(ms)
      else hasTime = false
    } else hasTime = false
  }
  if (coords.length < 2) throw new Error('GPX file contains no usable coordinates')

  const waypoints = [...doc.querySelectorAll('wpt')]
    .map((w) => ({
      name: w.querySelector('name')?.textContent?.trim() ?? 'Waypoint',
      lat: parseFloat(w.getAttribute('lat') ?? ''),
      lng: parseFloat(w.getAttribute('lon') ?? ''),
    }))
    .filter((w) => Number.isFinite(w.lat) && Number.isFinite(w.lng))

  return {
    name,
    coords,
    times: hasTime && times.length === coords.length ? times : undefined,
    waypoints: waypoints.length ? waypoints : undefined,
  }
}
